import { useEffect, useRef, useState } from 'react';
import { useMediaApplicationContext } from '../../../components/MediaApplicationContext';
import teceoSlickerLib, { NavigationDirection } from '../../../components/Slicker/TeceoSlickerLib';
import { handleDownloadMedia } from '../../../utils/downloadLink';
import { useTranslation } from '../../translation/TranslationContext';
import { MediasPreviewItem } from './MediasPreview';

function useMediaPreview<T extends MediasPreviewItem = MediasPreviewItem>(
  medias?: T[],
  selectedMedia?: T,
) {
  const mediaContext = useMediaApplicationContext();
  const { translate } = useTranslation();

  const TIMEOUT = 100;
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const navigationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const currentIndex =
      selectedMedia && medias
        ? Math.max(
            0,
            medias.findIndex(
              (media) => media.type === selectedMedia.type && media.url === selectedMedia.url,
            ),
          )
        : 0;

    setSelectedIndex(currentIndex);
  }, [medias, selectedMedia]);

  const handleNavigateDirection = (navigateDirection: NavigationDirection) => {
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
    }

    const nextIndex = teceoSlickerLib.getNextIndex(
      selectedIndex,
      navigateDirection,
      medias?.length ?? 0,
    );
    navigationTimeoutRef.current = setTimeout(() => {
      setSelectedIndex(nextIndex);
    }, TIMEOUT);
  };

  const handleOnClickToDownload = (media: MediasPreviewItem) => {
    handleDownloadMedia({ url: media.url, filename: media.fileName, translate });
  };

  const currentMedia = medias?.at(selectedIndex);

  const currentMediaUrl = mediaContext.getMediaFromSize(currentMedia?.url);

  return {
    translate,
    currentMedia,
    selectedIndex,
    currentMediaUrl,
    timeout: TIMEOUT,
    handleNavigateDirection,
    handleOnClickToDownload,
  };
}

export default useMediaPreview;
